<template>
  <div id="main_div">
    <!-- Charts Section -->
    <GestaoClientesCharts ref="chartsRef" />

    <!-- Tabs Section -->
    <TabView v-model="activeTabIndex" :scrollable="false">
      <!-- Custom button tablist (better UX, wraps, no scroll) -->
      <template #tablist>
        <div class="tab-buttons" role="tablist" data-testid="clientes-status-tabs">
          <Button size="small"
                  :severity="activeTabIndex === 0 ? 'primary' : 'secondary'"
                  :outlined="activeTabIndex !== 0"
                  data-testid="tab-clientes-sem-orcamento"
                  class="tab-button"
                  @click="activeTabIndex = 0">
            <Badge :value="totalClientesSemOrcamento" severity="secondary" />
            {{ t('Clientes sem orçamento') }}
            <i class="pi pi-info-circle detalhes-infos" v-tooltip="t('Clientes cadastrados no período contemplado pelo filtro que ainda não receberam um orçamento')"></i>
          </Button>

          <Button size="small"
                  :severity="activeTabIndex === 1 ? 'primary' : 'secondary'"
                  :outlined="activeTabIndex !== 1"
                  data-testid="tab-orcamentos-sem-compromisso"
                  class="tab-button"
                  @click="activeTabIndex = 1">
            <Badge :value="totalOrcamentosSemCompromisso" severity="secondary" />
            {{ t('Orçamentos sem compromissos') }}
            <i class="pi pi-info-circle detalhes-infos" v-tooltip="t('Orçamentos cadastrados no período contemplado pelo filtro que ainda não tiveram um compromisso agendado')"></i>
          </Button>

          <Button size="small"
                  :severity="activeTabIndex === 2 ? 'primary' : 'secondary'"
                  :outlined="activeTabIndex !== 2"
                  data-testid="tab-orcamentos-com-compromisso"
                  class="tab-button"
                  @click="activeTabIndex = 2">
            <Badge :value="totalOrcamentosComCompromisso" severity="secondary" />
            {{ t('Orçamentos com compromisso') }}
            <i class="pi pi-info-circle detalhes-infos" v-tooltip="t('Orçamentos com compromisso que foram cadastrados no período contemplado pelo filtro')"></i>
          </Button>

          <Button size="small"
                  :severity="activeTabIndex === 3 ? 'primary' : 'secondary'"
                  :outlined="activeTabIndex !== 3"
                  data-testid="tab-todos-clientes"
                  class="tab-button"
                  @click="activeTabIndex = 3">
            <Badge :value="totalClientes" severity="secondary" />
            {{ t('Todos os clientes') }}
            <i class="pi pi-info-circle detalhes-infos" v-tooltip="t('Todos os clientes das franquias do filtro, em qualquer data')"></i>
          </Button>

          <Button size="small"
                  :severity="activeTabIndex === 6 ? 'primary' : 'secondary'"
                  :outlined="activeTabIndex !== 6"
                  data-testid="tab-clientes-no-periodo"
                  class="tab-button"
                  @click="activeTabIndex = 6">
            <Badge :value="totalClientesNoPeriodo" severity="secondary" />
            {{ t('Clientes no período') }}
            <i class="pi pi-info-circle detalhes-infos" v-tooltip="t('Clientes cadastrados no período contemplado pelo filtro')"></i>
          </Button>

          <Button size="small"
                  :severity="activeTabIndex === 4 ? 'primary' : 'secondary'"
                  :outlined="activeTabIndex !== 4"
                  data-testid="tab-compromissos-cancelados"
                  class="tab-button"
                  @click="activeTabIndex = 4">
            <Badge :value="totalCompromissosCancelados" severity="secondary" />
            {{ t('Compromissos cancelados') }}
            <i class="pi pi-info-circle detalhes-infos" v-tooltip="t('Compromissos cancelados que foram cadastrados no período contemplado pelo filtro.')"></i>
          </Button>

          <Button size="small"
                  :severity="activeTabIndex === 5 ? 'primary' : 'secondary'"
                  :outlined="activeTabIndex !== 5"
                  data-testid="tab-clientes-arquivados"
                  class="tab-button"
                  @click="activeTabIndex = 5">
            <Badge :value="totalClientesArquivados" severity="secondary" />
            {{ t('Clientes arquivados') }}
            <i class="pi pi-info-circle detalhes-infos" v-tooltip="t('Clientes que foram arquivados')"></i>
          </Button>
        </div>
      </template>

      <!-- Tab panels -->
      <TabViewPanel :value="0">
        <GestaoClientesTabClientesSemOrcamento
          ref="clientesSemOrcamentoRef"
          :visible="true"
          @update:total="(total) => totalClientesSemOrcamento = total"
        />
      </TabViewPanel>

      <TabViewPanel :value="1">
        <GestaoClientesTabOrcamentosSemCompromisso
          ref="orcamentosSemCompromissoRef"
          :visible="true"
          @update:total="(total) => totalOrcamentosSemCompromisso = total"
        />
      </TabViewPanel>

      <TabViewPanel :value="2">
        <GestaoClientesTabOrcamentosComCompromisso
          ref="orcamentosComCompromissoRef"
          :visible="true"
          @update:total="(total) => totalOrcamentosComCompromisso = total"
        />
      </TabViewPanel>

      <TabViewPanel :value="3">
        <GestaoClientesTabClientes
          ref="clientesRef"
          :visible="true"
          @update:total="(total) => totalClientes = total"
        />
      </TabViewPanel>

      <TabViewPanel :value="4">
        <GestaoClientesTabCompromissosCancelados
          ref="compromissosCanceladosRef"
          :visible="true"
          @update:total="(total) => totalCompromissosCancelados = total"
        />
      </TabViewPanel>

      <TabViewPanel :value="5">
        <GestaoClientesTabClientesArquivados
          ref="clientesArquivadosRef"
          :visible="true"
          @update:total="(total) => totalClientesArquivados = total"
        />
      </TabViewPanel>

      <TabViewPanel :value="6">
        <GestaoClientesTabClientes
          ref="clientesNoPeriodoRef"
          :visible="true"
          :periodo="true"
          @update:total="(total) => totalClientesNoPeriodo = total"
        />
      </TabViewPanel>
    </TabView>

  <!-- All modals are centralized in ModalManager.vue and opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed, inject } from 'vue';
import { log } from '../../utils/DebugLogger';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import TabView from '../../ui/TabView.vue';
import TabViewPanel from '../../ui/TabViewPanel.vue';
import GestaoClientesCharts from './GestaoClientesCharts.vue';
import GestaoClientesTabClientesSemOrcamento from './GestaoClientesTabClientesSemOrcamento.vue';
import GestaoClientesTabOrcamentosSemCompromisso from './GestaoClientesTabOrcamentosSemCompromisso.vue';
import GestaoClientesTabOrcamentosComCompromisso from './GestaoClientesTabOrcamentosComCompromisso.vue';
import GestaoClientesTabClientes from './GestaoClientesTabClientes.vue';
import GestaoClientesTabCompromissosCancelados from './GestaoClientesTabCompromissosCancelados.vue';
import GestaoClientesTabClientesArquivados from './GestaoClientesTabClientesArquivados.vue';
import type { permissionsService as PermissionsServiceType } from '../../services/permissionsService';

type ClienteId = number | string;
type OrcamentoId = number | string;

interface TabRef {
  loadData: () => void;
}

interface ChartsRef {
  refreshAllCharts: () => void;
  loadDailyChart: () => void;
  loadMonthlyChart: () => void;
}

interface MensagensWizardPayload {
  selectedClients?: unknown[];
}

interface ClientPayload {
  clienteId?: ClienteId;
}

const permissionsService = inject<typeof PermissionsServiceType>('permissionsService');
const usuario = computed(() => permissionsService?.getUser() ?? null);

const activeTabIndex = ref(0);
const activeTab = ref<'clientes_sem_orcamento' | string>('clientes_sem_orcamento');

const totalClientesSemOrcamento = ref(0);
const totalOrcamentosSemCompromisso = ref(0);
const totalOrcamentosComCompromisso = ref(0);
const totalClientes = ref(0);
const totalClientesNoPeriodo = ref(0);
const totalCompromissosCancelados = ref(0);
const totalClientesArquivados = ref(0);

const chartsRef = ref<ChartsRef | null>(null);
const clientesSemOrcamentoRef = ref<TabRef | null>(null);
const orcamentosSemCompromissoRef = ref<TabRef | null>(null);
const orcamentosComCompromissoRef = ref<TabRef | null>(null);
const clientesRef = ref<TabRef | null>(null);
const clientesNoPeriodoRef = ref<TabRef | null>(null);
const compromissosCanceladosRef = ref<TabRef | null>(null);
const clientesArquivadosRef = ref<TabRef | null>(null);

const { t } = useI18n();

const loadClientesSemOrcamentoData = (): void => {
  log('GestaoClientesPage', 'loadClientesSemOrcamentoData', 'Calling tab component loadData');
  clientesSemOrcamentoRef.value?.loadData();
};

const loadOrcamentosSemCompromissoData = (): void => {
  log('GestaoClientesPage', 'loadOrcamentosSemCompromissoData', 'Calling tab component loadData');
  orcamentosSemCompromissoRef.value?.loadData();
};

const loadOrcamentosComCompromissoData = (): void => {
  log('GestaoClientesPage', 'loadOrcamentosComCompromissoData', 'Calling tab component loadData');
  orcamentosComCompromissoRef.value?.loadData();
};

const loadClientesData = (): void => {
  log('GestaoClientesPage', 'loadClientesData', 'Calling tab component loadData');
  clientesRef.value?.loadData();
};
const loadClientesNoPeriodoData = (): void => {
  log('GestaoClientesPage', 'loadClientesNoPeriodoData', 'Calling tab component loadData');
  clientesNoPeriodoRef.value?.loadData();
};

const loadCompromissosCanceladosData = (): void => {
  log('GestaoClientesPage', 'loadCompromissosCanceladosData', 'Calling tab component loadData');
  compromissosCanceladosRef.value?.loadData();
};

const loadClientesArquivadosData = (): void => {
  log('GestaoClientesPage', 'loadClientesArquivadosData', 'Calling tab component loadData');
  clientesArquivadosRef.value?.loadData();
};

const handleClienteSave = (): void => {
  log('GestaoClientesPage', 'handleClienteSave', 'Cliente saved, refreshing data');
  loadClientesSemOrcamentoData();
  loadOrcamentosSemCompromissoData();
  loadClientesData();
  loadClientesNoPeriodoData();
  loadCompromissosCanceladosData();
  loadClientesArquivadosData();
  chartsRef.value?.refreshAllCharts();
};

const handleOrcamentoSave = (): void => {
  log('GestaoClientesPage', 'handleOrcamentoSave', 'Orcamento saved, refreshing data');
  loadClientesSemOrcamentoData();
  loadOrcamentosSemCompromissoData();
  chartsRef.value?.loadDailyChart();
  chartsRef.value?.loadMonthlyChart();
};

const handleOrcamentoServicoSave = (): void => {
  log('GestaoClientesPage', 'handleOrcamentoServicoSave', 'Orcamento servico saved, refreshing data');
  loadOrcamentosSemCompromissoData();
};

const handleOrcamentoDelete = (): void => {
  log('GestaoClientesPage', 'handleOrcamentoDelete', 'Orcamento deleted, refreshing data');
  loadClientesSemOrcamentoData();
  loadOrcamentosSemCompromissoData();
  chartsRef.value?.loadDailyChart();
};

const handleCompromissoDelete = (): void => {
  log('GestaoClientesPage', 'handleCompromissoDelete', 'Compromisso deleted, refreshing data');
  loadOrcamentosSemCompromissoData();
  loadCompromissosCanceladosData();
  chartsRef.value?.loadDailyChart();
};

const handleCompromissoServicoSave = (): void => {
  log('GestaoClientesPage', 'handleCompromissoServicoSave', 'Compromisso servico saved, refreshing data');
};

const handleOpenMensagensWizard = (payload?: MensagensWizardPayload): void => {
  log('GestaoClientesPage', 'handleOpenMensagensWizard', 'Opening wizard via eventBus with clients', payload);
  eventBus.emit('modal.mensagens-programadas-wizard.open', {
    selectedClientes: payload?.selectedClients || []
  });
};

const handleFiltroOK = (): void => {
  log('GestaoClientesPage', 'handleFiltroOK', 'Filter applied, refreshing all data');
  loadClientesArquivadosData();
  loadClientesData();
  loadClientesNoPeriodoData();
  loadClientesSemOrcamentoData();
  loadOrcamentosSemCompromissoData();
  loadOrcamentosComCompromissoData();
  loadCompromissosCanceladosData();
};

const handleNewOrcamentoForClient = (clienteId?: ClienteId): void => {
  log('GestaoClientesPage', 'handleNewOrcamentoForClient', 'Opening orcamento modal for client via eventBus', clienteId);
  if (clienteId) {
    eventBus.emit('modal.orcamentos.open', { clienteId });
  }
};

const handleEditOrcamento = (orcamentoId?: OrcamentoId): void => {
  log('GestaoClientesPage', 'handleEditOrcamento', 'Opening orcamento modal for edit via eventBus', orcamentoId);
  if (orcamentoId) {
    eventBus.emit('modal.orcamentos.open', { id: orcamentoId });
  }
};

const handleOpenNewModalForClient = (payload?: ClientPayload): void => handleNewOrcamentoForClient(payload?.clienteId);
const handleOpenModalForClient = (payload?: ClientPayload): void => handleNewOrcamentoForClient(payload?.clienteId);

onMounted(() => {
  log('GestaoClientesPage', 'onMounted', 'Component mounted, setting up event listeners', {
    usuario: usuario.value,
    versaoSistema: usuario.value?.versaoSistema
  });

  eventBus.on('cliente.saved', handleClienteSave);
  eventBus.on('orcamento.saved', handleOrcamentoSave);
  eventBus.on('orcamentoservico.saved', handleOrcamentoServicoSave);
  eventBus.on('orcamento.deleted', handleOrcamentoDelete);
  eventBus.on('compromisso.deleted', handleCompromissoDelete);
  eventBus.on('compromisso.servico.saved', handleCompromissoServicoSave);
  eventBus.on('filter.updated', handleFiltroOK);

  eventBus.on('orcamento.newForClientRequested', handleNewOrcamentoForClient);
  eventBus.on('orcamento.openNewModalForClient', handleOpenNewModalForClient);
  eventBus.on('orcamento.open-modal-for-client', handleOpenModalForClient);

  eventBus.on('orcamento.edit', handleEditOrcamento);

  eventBus.on('mensagens-programadas.open-modal', handleOpenMensagensWizard);

  handleFiltroOK();
});

onBeforeUnmount(() => {
  log('GestaoClientesPage', 'onBeforeUnmount', 'Cleaning up event listeners');

  eventBus.off('cliente.saved', handleClienteSave);
  eventBus.off('orcamento.saved', handleOrcamentoSave);
  eventBus.off('orcamentoservico.saved', handleOrcamentoServicoSave);
  eventBus.off('orcamento.deleted', handleOrcamentoDelete);
  eventBus.off('compromisso.deleted', handleCompromissoDelete);
  eventBus.off('compromisso.servico.saved', handleCompromissoServicoSave);
  eventBus.off('filter.updated', handleFiltroOK);

  eventBus.off('orcamento.newForClientRequested', handleNewOrcamentoForClient);
  eventBus.off('orcamento.openNewModalForClient', handleOpenNewModalForClient);
  eventBus.off('orcamento.open-modal-for-client', handleOpenModalForClient);
  eventBus.off('orcamento.edit', handleEditOrcamento);
  eventBus.off('mensagens-programadas.open-modal', handleOpenMensagensWizard);
});
</script>

<style scoped>
#main_div {
  padding: 0;
}

:deep(.p-tabs) {
  margin-bottom: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.tab-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 10px;
  border-bottom: 2px solid #dee2e6;
  margin-bottom: 10px;
}

.tab-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

:deep(.p-tabs .p-tablist) {
  background: transparent;
  border-bottom: 2px solid #dee2e6;
  margin: 0;
  padding: 10px 10px 0 10px;
  flex-wrap: wrap;
  overflow-x: visible !important;
}

/* Hide scroll navigation buttons */
:deep(.p-tablist-nav-button),
:deep(.p-tablist-next-button),
:deep(.p-tablist-prev-button) {
  display: none !important;
}

:deep(.p-tabs .p-tab) {
  padding: 0.5rem 0.75rem;
  font-weight: 500;
  color: #444;
  border-top: 3px solid transparent;
  margin-right: 5px;
  margin-bottom: -2px;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  font-size: 0.875rem;
  flex-shrink: 0;
}

:deep(.p-tabs .p-tab.p-tab-active) {
  border-top-color: #3c8dbc;
  border-left-color: #f4f4f4;
  border-right-color: #f4f4f4;
}

:deep(.p-tabs .p-tabpanels) {
  background: #fff;
  padding: 10px;
  border-bottom-right-radius: 8px;
  border-bottom-left-radius: 8px;
}

:deep(.p-tabs .p-tabpanel) {
  padding: 0;
}

:deep(.p-badge) {
  margin-right: 8px;
  min-width: 1.5rem;
}

.detalhes-infos {
  cursor: help;
  margin-left: 5px;
  color: #999;
  font-size: 0.875rem;
}

.detalhes-infos:hover {
  color: #666;
}

/* Responsive adjustments for smaller screens */
@media (max-width: 1400px) {
  :deep(.p-tabs .p-tab) {
    padding: 0.5rem 0.6rem;
    font-size: 0.8rem;
    gap: 4px;
  }

  :deep(.p-badge) {
    margin-right: 4px;
    font-size: 0.75rem;
  }

  .detalhes-infos {
    font-size: 0.75rem;
    margin-left: 3px;
  }
}

@media (max-width: 1200px) {
  :deep(.p-tabs .p-tab) {
    padding: 0.4rem 0.5rem;
    font-size: 0.75rem;
  }
}
</style>
